import React, { useEffect, useRef } from 'react';
import { AlertTriangle } from 'lucide-react';
// @ts-ignore - plotly.js-dist-min doesn't have type declarations
import Plotly from 'plotly.js-dist-min';
import { RegressionData } from '../../../services/ChartService';

interface RegressionChartProps {
  title: string;
  data: RegressionData;
  height?: string;
}

export const RegressionChart: React.FC<RegressionChartProps> = ({
  title,
  data,
  height = '500px'
}) => {
  const scatterChartRef = useRef<HTMLDivElement>(null);
  const corrChartRef = useRef<HTMLDivElement>(null);

  // Render scatter plot with regression line
  useEffect(() => {
    if (!scatterChartRef.current || !data.x_values || data.x_values.length === 0) return;

    const traces: any[] = [];

    // Determine axis labels based on available data
    // Priority: 1) Use field names if same symbol, 2) Use symbols if different, 3) Fallback to title parsing
    let yLabel: string;
    let xLabel: string;
    
    if (data.y_symbol === data.x_symbols?.[0] && data.y_field && data.x_fields?.[0]) {
      // Same symbol, different fields (e.g., SPY option price vs IV)
      // Use field names directly
      yLabel = data.y_field;
      xLabel = data.x_fields[0];
    } else if (data.y_symbol && data.x_symbols?.[0] && data.y_symbol !== data.x_symbols[0]) {
      // Different symbols (e.g., SPY vs QQQ)
      // Use symbol names with "Returns" suffix
      yLabel = `${data.y_symbol} Returns`;
      xLabel = `${data.x_symbols[0]} Returns`;
    } else {
      // Fallback: try to parse from title
      // Format: "SPY option_price vs iv" or "SPY vs QQQ"
      const fieldMatch = title.match(/^([A-Z]+)\s+(\w+)\s+vs\s+(\w+)/i);
      const symbolMatch = title.match(/^([A-Z]+)\s+vs\s+([A-Z]+)/i);
      
      if (fieldMatch) {
        yLabel = fieldMatch[2];
        xLabel = fieldMatch[3];
      } else if (symbolMatch) {
        yLabel = `${symbolMatch[1]} Returns`;
        xLabel = `${symbolMatch[2]} Returns`;
      } else {
        // Generic fallback
        yLabel = 'Y (Dependent Variable)';
        xLabel = 'X (Independent Variable)';
      }
    }

    // Scatter plot of actual data points
    traces.push({
      type: 'scatter',
      mode: 'markers',
      x: data.x_values,
      y: data.y_actual,
      name: 'Actual Data',
      marker: {
        color: '#2196F3',
        size: 8,
        opacity: 0.6,
        line: {
          color: '#1976D2',
          width: 1
        }
      },
      hovertemplate: 
        '<b>Actual</b><br>' +
        'X: %{x:.4f}<br>' +
        'Y: %{y:.4f}<br>' +
        '<extra></extra>'
    });

    // Regression line (sort by x for proper line drawing)
    const sortedIndices = data.x_values!
      .map((x, i) => ({ x, y_pred: data.y_predicted![i], i }))
      .sort((a, b) => a.x - b.x);
    
    const sortedX = sortedIndices.map(d => d.x);
    const sortedYPred = sortedIndices.map(d => d.y_pred);

    traces.push({
      type: 'scatter',
      mode: 'lines',
      x: sortedX,
      y: sortedYPred,
      name: 'Regression Line',
      line: {
        color: '#F44336',
        width: 3
      },
      hovertemplate: 
        '<b>Predicted</b><br>' +
        'X: %{x:.4f}<br>' +
        'Y: %{y:.4f}<br>' +
        '<extra></extra>'
    });

    const layout = {
      title: {
        text: `${title}<br><sub>Beta = ${(data.slopes[0] || 0).toFixed(4)}, R² = ${data.r_squared.toFixed(4)}</sub>`,
        font: { size: 16, weight: 'bold' }
      },
      xaxis: {
        title: {
          text: xLabel,
          font: { size: 14, weight: 'bold' }
        },
        zeroline: true,
        zerolinecolor: '#999',
        zerolinewidth: 1,
        gridcolor: '#e0e0e0'
      },
      yaxis: {
        title: {
          text: yLabel,
          font: { size: 14, weight: 'bold' }
        },
        zeroline: true,
        zerolinecolor: '#999',
        zerolinewidth: 1,
        gridcolor: '#e0e0e0'
      },
      showlegend: true,
      legend: {
        x: 0.02,
        y: 0.98,
        xanchor: 'left',
        yanchor: 'top',
        bgcolor: 'rgba(255, 255, 255, 0.95)',
        bordercolor: '#333',
        borderwidth: 2,
        font: {
          size: 12,
          family: 'Arial, sans-serif'
        }
      },
      hovermode: 'closest',
      margin: { t: 80, b: 60, l: 60, r: 40 },
      autosize: true,
      plot_bgcolor: '#fafafa'
    };

    const config = {
      responsive: true,
      displayModeBar: true,
      displaylogo: false,
      modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'select2d']
    };

    Plotly.react(scatterChartRef.current, traces, layout, config);

    // Cleanup
    return () => {
      if (scatterChartRef.current) {
        Plotly.purge(scatterChartRef.current);
      }
    };
  }, [title, data]);

  // Render correlation matrix heatmap (if multiple X variables)
  useEffect(() => {
    if (!corrChartRef.current || data.correlation_matrix.length <= 1) return;

    const trace = {
      type: 'heatmap',
      z: data.correlation_matrix,
      x: data.x_symbols,
      y: data.x_symbols,
      colorscale: 'RdBu',
      zmid: 0,
      zmin: -1,
      zmax: 1,
      text: data.correlation_matrix.map(row => 
        row.map(val => val.toFixed(2))
      ),
      texttemplate: '%{text}',
      textfont: { size: 12 },
      hovertemplate: 
        '%{y} vs %{x}<br>' +
        'Correlation: %{z:.3f}<br>' +
        '<extra></extra>'
    };

    const layout = {
      title: 'Check for Multicollinearity',
      xaxis: { side: 'bottom' },
      yaxis: { autorange: 'reversed' },
      margin: { t: 40, b: 60, l: 80, r: 40 },
      height: 300,
      autosize: true
    };

    const config = {
      responsive: true,
      displayModeBar: false
    };

    Plotly.react(corrChartRef.current, [trace], layout, config);

    // Cleanup
    return () => {
      if (corrChartRef.current) {
        Plotly.purge(corrChartRef.current);
      }
    };
  }, [data]);

  return (
    <div style={{ width: '100%' }}>
      {/* Scatter Plot with Regression Line */}
      <div ref={scatterChartRef} style={{ width: '100%', height }} />
      
      {/* Correlation Matrix (if multiple X variables) */}
      {data.correlation_matrix && data.correlation_matrix.length > 1 && (
        <div style={{ marginTop: '20px' }}>
          <h4>Correlation Matrix (X Variables)</h4>
          <div ref={corrChartRef} style={{ width: '100%', height: '300px' }} />
          <p style={{ fontSize: '12px', color: '#666', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <AlertTriangle size={14} aria-hidden="true" />
            High correlations (&gt; 0.8) indicate multicollinearity
          </p>
        </div>
      )}
    </div>
  );
};
